import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

type Suppliers = { event: { id: number; name: string }; suppliers: string[] };

/**
 * Écran d'export (plan §5.12). Les téléchargements sont de simples liens vers
 * des routes qui renvoient un CSV en pièce jointe : le navigateur télécharge,
 * rien à gérer côté JS, et la session cookie authentifie la requête.
 */
export default function Export() {
  const { data, isPending } = useQuery<Suppliers>({
    queryKey: ['admin', 'export', 'suppliers'],
    queryFn: () => api('/api/admin/export/suppliers'),
  });

  return (
    <div className="space-y-6">
      <section className="bg-card rounded-xl border p-6">
        <h2 className="mb-1 text-lg font-semibold">Export global</h2>
        <p className="text-muted-foreground mb-4 text-sm">
          Commandes soumises de {isPending ? '…' : data?.event.name}. Fichiers UTF-8 (BOM) prêts
          pour Excel.
        </p>
        <div className="flex flex-wrap gap-3">
          <LienExport href="/api/admin/export/orders.csv" titre="Commandes" sous="une ligne par commande" />
          <LienExport href="/api/admin/export/lines.csv" titre="Lignes de commande" sous="le détail produit" />
        </div>
      </section>

      <section className="bg-card rounded-xl border p-6">
        <h2 className="mb-1 text-lg font-semibold">Par fournisseur</h2>
        <p className="text-muted-foreground mb-4 text-sm">
          Un fichier par fournisseur, à remettre à chaque représentant en fin de show.
        </p>

        {isPending ? (
          <p className="text-muted-foreground text-sm">Chargement…</p>
        ) : data && data.suppliers.length > 0 ? (
          <div className="flex flex-wrap gap-3">
            {data.suppliers.map((code) => (
              <LienExport
                key={code}
                href={`/api/admin/export/supplier/${code}.csv`}
                titre={code}
                sous="lignes vendues"
              />
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">
            Aucune vente à exporter pour l'instant.
          </p>
        )}
      </section>
    </div>
  );
}

function LienExport({ href, titre, sous }: { href: string; titre: string; sous: string }) {
  return (
    <a
      href={href}
      // download laisse le serveur nommer le fichier via Content-Disposition.
      download
      className="border-input hover:border-primary/50 flex min-w-44 flex-col rounded-lg border p-4"
    >
      <span className="font-semibold">{titre}</span>
      <span className="text-muted-foreground text-sm">{sous}</span>
      <span className="text-primary mt-2 text-sm font-medium">Télécharger le CSV ↓</span>
    </a>
  );
}
